import type { IconProps } from '@phosphor-icons/react'
import { forwardRef } from 'react'
import { cn } from './primitives'

/**
 * One coloured mark per finance screen (24-unit grid), drawn inline like ShipmentTypeIcon so the colour says the
 * screen before the word does. Brand-independent colours; each mark is a Phosphor-compatible component (size,
 * weight and className props) so it slots into PageHeader, KpiCard and the navigation without adapters.
 */
export type FinanceIconKey =
  | 'payToCustomer' | 'cod' | 'runnerPayment' | 'invoices' | 'branchPayments' | 'statements' | 'priceList' | 'products'
  | 'services' | 'clients' | 'addClient' | 'commission' | 'paymentReview' | 'collections' | 'pendingCollections' | 'agentCollections' | 'payoutRadar'

export const FINANCE_ICON_TONE: Record<FinanceIconKey, { fg: string; bg: string }> = {
  payToCustomer: { fg: '#16a34a', bg: '#e7f6ef' },
  cod: { fg: '#d97706', bg: '#fdf2e0' },
  runnerPayment: { fg: '#ea580c', bg: '#feeee3' },
  invoices: { fg: '#2563eb', bg: '#e6eefc' },
  branchPayments: { fg: '#0d9488', bg: '#e0f5f2' },
  statements: { fg: '#4f46e5', bg: '#eae9fd' },
  priceList: { fg: '#db2777', bg: '#fde8f1' },
  products: { fg: '#7c3aed', bg: '#efe8fd' },
  services: { fg: '#0891b2', bg: '#e0f4f8' },
  clients: { fg: '#0284c7', bg: '#e0f0fb' },
  addClient: { fg: '#16a34a', bg: '#e7f6ef' },
  commission: { fg: '#b45309', bg: '#fbefdc' },
  paymentReview: { fg: '#059669', bg: '#e1f5ee' },
  collections: { fg: '#ca8a04', bg: '#fbf4dc' },
  pendingCollections: { fg: '#dc2626', bg: '#fdeaea' },
  agentCollections: { fg: '#9333ea', bg: '#f1e7fd' },
  payoutRadar: { fg: '#AE1A1C', bg: '#fbe9e9' },
}

type MarkProps = IconProps

const S = { strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

function paths(key: FinanceIconKey, fg: string) {
  switch (key) {
    case 'payToCustomer':
      // banknotes handed to a person
      return (<>
        <rect x="2.5" y="8" width="13" height="8.5" rx="1.8" fill={fg} opacity=".18" stroke={fg} {...S} />
        <circle cx="9" cy="12.25" r="2" fill={fg} />
        <path d="M17 6.5h3.5v10" stroke={fg} {...S} />
        <path d="M16 20.5c1.5-2.2 3.2-3 5.5-3" stroke={fg} {...S} />
      </>)
    case 'cod':
      // a coin stack with a delivered tick
      return (<>
        <ellipse cx="10" cy="7" rx="6.5" ry="2.6" fill={fg} opacity=".2" stroke={fg} {...S} />
        <path d="M3.5 7v4c0 1.4 2.9 2.6 6.5 2.6S16.5 12.4 16.5 11V7" stroke={fg} {...S} />
        <path d="M3.5 11v4c0 1.4 2.9 2.6 6.5 2.6 1 0 2-.1 2.8-.3" stroke={fg} {...S} />
        <circle cx="17.5" cy="17" r="4.3" fill={fg} />
        <path d="m15.6 17 1.3 1.3 2.5-2.6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </>)
    case 'runnerPayment':
      // a runner's helmet over a banknote: cash the runner hands in
      return (<>
        <path d="M5 11a7 7 0 0 1 14 0v1H5v-1Z" fill={fg} opacity=".2" stroke={fg} {...S} />
        <path d="M4 12h16" stroke={fg} {...S} />
        <rect x="3" y="15" width="18" height="6" rx="1.6" fill={fg} opacity=".18" stroke={fg} {...S} />
        <circle cx="12" cy="18" r="1.6" fill={fg} />
      </>)
    case 'invoices':
      // a document with money lines and a stamp
      return (<>
        <path d="M6 2.8h8.5L19 7.3v13.2a1.2 1.2 0 0 1-1.2 1.2H6a1.2 1.2 0 0 1-1.2-1.2V4A1.2 1.2 0 0 1 6 2.8Z" fill={fg} opacity=".16" stroke={fg} {...S} />
        <path d="M14.5 2.8v4.5H19" stroke={fg} {...S} />
        <path d="M8 11h8M8 14.5h8M8 18h4" stroke={fg} {...S} />
        <circle cx="16.5" cy="18" r="2.6" fill={fg} />
      </>)
    case 'branchPayments':
      // a branch building with a coin
      return (<>
        <path d="M3.5 9.5 12 4l8.5 5.5" stroke={fg} {...S} />
        <path d="M5 9.5h14V20H5z" fill={fg} opacity=".16" stroke={fg} {...S} />
        <path d="M8.5 20v-6h3v6M15.5 13h2M15.5 16h2" stroke={fg} {...S} />
        <circle cx="18.5" cy="19" r="3.4" fill={fg} />
        <path d="M18.5 17.3v3.4M17.2 19h2.6" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
      </>)
    case 'statements':
      // a ledger with a running balance
      return (<>
        <rect x="4" y="3" width="16" height="18" rx="2" fill={fg} opacity=".16" stroke={fg} {...S} />
        <path d="M8 3v18" stroke={fg} {...S} />
        <path d="M11 8h6M11 12h6M11 16h3.5" stroke={fg} {...S} />
        <circle cx="17.5" cy="17.5" r="2.5" fill={fg} />
      </>)
    case 'priceList':
      // a price tag
      return (<>
        <path d="M3.5 11.5V5A1.5 1.5 0 0 1 5 3.5h6.5l9 9-8 8-9-9Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <circle cx="8" cy="8" r="1.6" fill={fg} />
        <path d="M11 14.5 14.5 11" stroke={fg} {...S} />
      </>)
    case 'products':
      // stacked boxes
      return (<>
        <path d="M4 12.5 12 8.5l8 4v6l-8 4-8-4v-6Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="m4 12.5 8 4 8-4M12 16.5v6" stroke={fg} {...S} />
        <path d="M7.5 6.3 12 4l4.5 2.3" stroke={fg} {...S} />
      </>)
    case 'services':
      // a wrench inside a gear
      return (<>
        <circle cx="12" cy="12" r="8" fill={fg} opacity=".16" stroke={fg} {...S} />
        <path d="M15.3 8.7a2.6 2.6 0 0 1-3.3 3.3l-3.2 3.2a1.1 1.1 0 0 1-1.6-1.6l3.2-3.2a2.6 2.6 0 0 1 3.3-3.3l-1.5 1.5 1.6 1.6 1.5-1.5Z" fill={fg} />
        <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2" stroke={fg} {...S} />
      </>)
    case 'clients':
      // a storefront with people
      return (<>
        <path d="M4 9.5 5.5 4h13L20 9.5" stroke={fg} {...S} />
        <path d="M4 9.5c0 1.5 1.2 2.5 2.7 2.5S9.3 11 9.3 9.5c0 1.5 1.2 2.5 2.7 2.5s2.7-1 2.7-2.5c0 1.5 1.2 2.5 2.7 2.5S20 11 20 9.5" stroke={fg} {...S} />
        <path d="M5.5 12v8h13v-8" fill={fg} opacity=".16" stroke={fg} {...S} />
        <path d="M9.5 20v-5h5v5" stroke={fg} {...S} />
      </>)
    case 'addClient':
      return (<>
        <circle cx="10" cy="8" r="3.8" fill={fg} opacity=".2" stroke={fg} {...S} />
        <path d="M3.5 20c.5-4 3.3-6 6.5-6 1.3 0 2.5.3 3.5.9" stroke={fg} {...S} />
        <circle cx="18" cy="17" r="4.3" fill={fg} />
        <path d="M18 14.8v4.4M15.8 17h4.4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
      </>)
    case 'commission':
      // percent sign over a truck wheel
      return (<>
        <circle cx="12" cy="12" r="8.5" fill={fg} opacity=".16" stroke={fg} {...S} />
        <path d="m8.5 15.5 7-7" stroke={fg} strokeWidth="2" strokeLinecap="round" />
        <circle cx="9" cy="9" r="1.8" fill={fg} />
        <circle cx="15" cy="15" r="1.8" fill={fg} />
      </>)
    case 'paymentReview':
      // a bar chart with a magnifier
      return (<>
        <rect x="3" y="12" width="3.5" height="8" rx=".8" fill={fg} opacity=".5" />
        <rect x="8.5" y="7" width="3.5" height="13" rx=".8" fill={fg} opacity=".75" />
        <rect x="14" y="4" width="3.5" height="16" rx=".8" fill={fg} opacity=".35" />
        <circle cx="17.5" cy="15.5" r="3.4" fill="#fff" stroke={fg} {...S} />
        <path d="m20 18 2 2" stroke={fg} strokeWidth="2" strokeLinecap="round" />
      </>)
    case 'collections':
      // a hand receiving coins
      return (<>
        <circle cx="12" cy="6" r="3.2" fill={fg} opacity=".25" stroke={fg} {...S} />
        <path d="M12 4.5v3M10.8 6h2.4" stroke={fg} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M3.5 15.5 7 13.5c1-.6 2.3-.5 3.2.2l2.6 2.1a1.4 1.4 0 0 1-1.7 2.2l-2.3-1.3" stroke={fg} {...S} />
        <path d="M12 16.5 17 13c1-.7 2.4-.5 3.2.4L21 14.5l-6.5 5.3-4.4.5-6.6-3.8" fill={fg} opacity=".18" stroke={fg} {...S} />
      </>)
    case 'pendingCollections':
      // an hourglass on a coin
      return (<>
        <circle cx="12" cy="12" r="9" fill={fg} opacity=".14" stroke={fg} {...S} />
        <path d="M8.5 6.5h7M8.5 17.5h7M9.5 6.5c0 3 2.5 4.2 2.5 5.5s-2.5 2.5-2.5 5.5M14.5 6.5c0 3-2.5 4.2-2.5 5.5s2.5 2.5 2.5 5.5" stroke={fg} {...S} />
        <path d="M10.4 16.5h3.2L12 14.6Z" fill={fg} />
      </>)
    case 'agentCollections':
      // a handshake with a coin
      return (<>
        <path d="M2.5 9.5 6 7l4.5 1.2L14 6l3.5 2.5 4 1v6.5l-4.5 4L12 21l-5-2.5-4.5-3.5V9.5Z" fill={fg} opacity=".16" />
        <path d="M2.5 9.5 6 7l4.5 1.2M21.5 9.5 17.5 8.5 14 6l-4 4 1.6 1.4a1.6 1.6 0 0 0 2.2 0L15 10.5l5 4" stroke={fg} {...S} />
        <path d="M2.5 15.5 7 18.5l4.6 2.2L15.5 18" stroke={fg} {...S} />
        <circle cx="18.5" cy="18.5" r="3" fill={fg} />
      </>)
    case 'payoutRadar':
      // a radar sweep over a coin with an alert dot: money that does not add up
      return (<>
        <circle cx="11" cy="12" r="9" fill={fg} opacity=".12" stroke={fg} {...S} />
        <circle cx="11" cy="12" r="5" stroke={fg} {...S} opacity=".55" />
        <path d="M11 12 17.4 5.6" stroke={fg} {...S} />
        <path d="M11 3a9 9 0 0 1 6.4 2.6L11 12Z" fill={fg} opacity=".28" />
        <circle cx="11" cy="12" r="1.6" fill={fg} />
        <circle cx="19" cy="18" r="3.6" fill={fg} />
        <path d="M19 16.3v2M19 19.7v.01" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
      </>)
  }
}

function make(key: FinanceIconKey) {
  const c = FINANCE_ICON_TONE[key]
  const Mark = forwardRef<SVGSVGElement, MarkProps>(function FinanceMark({ size = 16, className, weight: _w, mirrored: _m, alt, color: _c, ...rest }, ref) {
    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden={alt ? undefined : true} aria-label={alt} focusable={false} className={cn('keep-color shrink-0', className)} {...rest}>
        {paths(key, c.fg)}
      </svg>
    )
  })
  Mark.displayName = `FinanceIcon.${key}`
  return Mark
}

/** Phosphor-compatible components, one per finance screen: `<FINANCE_ICONS.invoices size={20} />`. */
export const FINANCE_ICONS = Object.fromEntries((Object.keys(FINANCE_ICON_TONE) as FinanceIconKey[]).map((k) => [k, make(k)])) as Record<FinanceIconKey, ReturnType<typeof make>>

/** The mark alone by key, for places that pick the icon at runtime. */
export function FinanceIcon({ name, size = 16, className }: { name: FinanceIconKey; size?: number; className?: string }) {
  const I = FINANCE_ICONS[name]
  return <I size={size} className={className} />
}

/** Mark on its tinted square: the page-header treatment. */
export function FinanceIconTile({ name, size = 44 }: { name: FinanceIconKey; size?: number }) {
  const c = FINANCE_ICON_TONE[name]
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-2xl" style={{ width: size, height: size, background: c.bg }}>
      <FinanceIcon name={name} size={Math.round(size * 0.58)} />
    </span>
  )
}
